import { Icon } from "@iconify/react";
import { sentenceCase } from "change-case";
import React, { useState, useMemo, useEffect } from "react";
import plusFill from "@iconify/icons-eva/plus-fill";
import { formatRut, RutFormat } from "@fdograph/rut-utilities";
import { useNavigate } from "react-router-dom";

import {
  Avatar,
  Box,
  Button,
  Card,
  Container,
  Grid,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Titulo } from "../components/Titulo";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import Scrollbar from "../components/Scrollbar";
import SearchNotFound from "../components/SearchNotFound";
import { TablaHead } from "../components/_dashboard/tablas";
import * as locales from "@material-ui/core/locale";
import Page from "../components/Page";
import Label from "../components/Label";
import EmpleadoFormDialog from "../components/gestionEmpleado/EmpleadoFormDialog";
import { Alerta } from "../components/Alerta";
import StoreIcon from "@material-ui/icons/Store";
import SucursalesEmpleado from "src/components/AdministrarEmpleados/SucursalesEmpleado";
import { useSelector } from "react-redux";
import { empleadosApi } from "src/services/empleadosApi";
import { useHistorialVisita } from "src/hooks/useHistorialVisita";
import useEmpleadosFilters from "../components/AdministrarEmpleados/hooks/useEmpleadosFilters";
import BarraFiltrosEmpleados from "../components/AdministrarEmpleados/BarraFiltrosEmpleados";

const TABLE_HEAD = [
  { id: "nombre", label: "Empleado", alignRight: false },
  { id: "cargo", label: "Cargo", alignRight: false },
  { id: "rut", label: "Rut", alignRight: false },
  { id: "telefono", label: "Teléfono", alignRight: false },
  { id: "correo", label: "Correo", alignRight: false },
  { id: "estado", label: "Estado", alignRight: false },
  { id: "" },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export default function Empleados() {
  const {
    sucursal: sucursalObtenida,
    rol: rolObtenido,
    empleado: user,
  } = useSelector((state) => state.auth.user);
  const navigate = useNavigate();

  const sucursal = sucursalObtenida.refSucursal;
  const [showAlert, setShowAlert] = useState(false);
  const [open, setOpen] = useState(false);
  const [openRelacionSucursal, setOpenRelacionSucursal] = useState(false);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState("nombre");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [elementoSeleccionado, setElementoSeleccionado] = useState(null);
  const [locale] = useState("esES");

  const { data: empleados = [], isLoading: loadingTable } =
    empleadosApi.useGetEmpleadosQuery(sucursal);

  useHistorialVisita(user);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = empleados.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const { filtros, filtrados, updateFiltro, limpiarFiltros } =
    useEmpleadosFilters(empleados);

  const sortedFiltered = useMemo(() => {
    const sorted = [...filtrados].sort(getComparator(order, orderBy));
    return sorted;
  }, [filtrados, order, orderBy]);

  // Si los filtros reducen el listado por debajo de la página actual, volver a la primera página.
  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(sortedFiltered.length / rowsPerPage) - 1,
    );
    if (page > maxPage) setPage(0);
  }, [sortedFiltered.length, rowsPerPage, page]);

  const emptyRows =
    page > 0
      ? Math.max(0, (1 + page) * rowsPerPage - sortedFiltered.length)
      : 0;

  const isUserNotFound = sortedFiltered.length === 0;

  return (
    <Page title="TAXTIC">
      <Container maxWidth="xl">
        <Titulo>
          <Grid container>
            <Grid item xs md></Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h4" gutterBottom>
                Administración de Empleados
              </Typography>
            </Grid>
            <Grid item xs md align="end">
              {["SUPER_ADMIN", "ADMIN"].includes(rolObtenido) && (
                <Button
                  onClick={() => {
                    setOpen(true);
                  }}
                  variant="contained"
                  startIcon={<Icon icon={plusFill} />}
                >
                  Agregar Empleado
                </Button>
              )}
            </Grid>
          </Grid>
        </Titulo>

        <Card>
          <BarraFiltrosEmpleados
            filtros={filtros}
            updateFiltro={updateFiltro}
            limpiarFiltros={limpiarFiltros}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              {!loadingTable ? (
                <Table>
                  <TablaHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={empleados.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                  />
                  <TableBody>
                    {sortedFiltered
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((row, idx) => {
                        const {
                          nombre,
                          apellidos,
                          rut,
                          telefono,
                          correo,
                          estado,
                          cargo,
                          foto_url,
                        } = row;
                        const iniciales = nombre
                          ? nombre.charAt(0).toUpperCase()
                          : "?";
                        const navegar = () => {
                          if (["SUPER_ADMIN", "ADMIN"].includes(rolObtenido)) {
                            navigate(`/dashboard/empleados/${rut}`);
                          }
                        };
                        return (
                          <TableRow hover key={rut + "_" + idx} tabIndex={-1}>
                            <TableCell
                              onClick={navegar}
                              sx={{ cursor: "pointer" }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                }}
                              >
                                <Avatar
                                  src={foto_url}
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    bgcolor: "#d57a23",
                                    fontSize: 14,
                                  }}
                                >
                                  {iniciales}
                                </Avatar>
                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {nombre}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {apellidos}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell
                              onClick={navegar}
                              sx={{ cursor: "pointer" }}
                            >
                              {cargo || "—"}
                            </TableCell>
                            <TableCell
                              onClick={navegar}
                              sx={{ cursor: "pointer" }}
                            >
                              {formatRut(rut, RutFormat.DASHS)}
                            </TableCell>
                            <TableCell
                              onClick={navegar}
                              sx={{ cursor: "pointer" }}
                            >
                              {telefono}
                            </TableCell>
                            <TableCell
                              onClick={navegar}
                              sx={{ cursor: "pointer" }}
                            >
                              {correo}
                            </TableCell>
                            <TableCell>
                              <Label
                                variant="ghost"
                                color={
                                  estado === "INACTIVO" ? "error" : "success"
                                }
                              >
                                {sentenceCase(estado)}
                              </Label>
                            </TableCell>
                            <TableCell align="right">
                              {rolObtenido === "SUPER_ADMIN" && (
                                <Tooltip title="Sucursales">
                                  <Button
                                    color="info"
                                    onClick={() => {
                                      setElementoSeleccionado(row);
                                      setOpenRelacionSucursal(true);
                                    }}
                                  >
                                    <StoreIcon />
                                  </Button>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={7} />
                      </TableRow>
                    )}
                  </TableBody>
                  {isUserNotFound && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={7} sx={{ py: 3 }}>
                          <SearchNotFound searchQuery={filtros.busqueda} />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              ) : (
                <Skeleton variant="rectangular" width="100%" height="500px" />
              )}
            </TableContainer>
          </Scrollbar>

          <ThemeProvider
            theme={(outerTheme) => createTheme(outerTheme, locales[locale])}
          >
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={sortedFiltered.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </ThemeProvider>
        </Card>
      </Container>
      <Alerta
        showAlert={showAlert}
        setShowAlert={setShowAlert}
        color="success"
        message="Datos del empleado guardados"
      />
      {open && (
        <EmpleadoFormDialog
          open={open}
          onClose={() => setOpen(false)}
          mode="create"
          defaultValues={{ estado: "ACTIVO" }}
        />
      )}

      {openRelacionSucursal && (
        <SucursalesEmpleado
          open={openRelacionSucursal}
          setOpen={setOpenRelacionSucursal}
          empleadoDato={elementoSeleccionado}
        />
      )}
    </Page>
  );
}
